import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown } from 'lucide-react';

/**
 * SearchableSelect - Autocomplete/combobox with portal dropdown
 * Dropdown renders in a portal to avoid overflow clipping issues.
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Buscar...',
  displayValue = '',
  disabled = false,
  className = '',
  'data-testid': testId,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const updatePos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) updatePos();
  }, [open, updatePos]);

  const filtered = options.filter(opt => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (opt.label || '').toLowerCase().includes(s) ||
           String(opt.value).includes(s) ||
           (opt.secondary || '').toLowerCase().includes(s);
  });

  // Show first 30 results, or all filtered when searching
  const showFiltered = !search && options.length > 50 ? options.slice(0, 30) : filtered.slice(0, 50);

  const handleSelect = (opt) => {
    onChange(opt.value, opt);
    setSearch('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null, null);
    setSearch('');
  };

  const showValue = displayValue || options.find(o => o.value === value)?.label || '';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={open ? search : showValue}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setSearch(''); updatePos(); }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full h-8 rounded-lg border bg-background pl-7 pr-7 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid={testId}
        />
        {value && !disabled ? (
          <button
            onClick={handleClear}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        ) : (
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        )}
      </div>

      {open && !disabled && createPortal(
        <div
          ref={dropdownRef}
          className="fixed max-h-48 overflow-y-auto rounded-lg border bg-popover shadow-xl"
          style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
        >
          {showFiltered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">{options.length > 50 && !search ? 'Escriba para buscar...' : 'Sin resultados'}</p>
          ) : (
            showFiltered.slice(0, 50).map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors flex items-center gap-2 ${
                  opt.value === value ? 'bg-primary/5 font-medium' : ''
                }`}
              >
                {opt.prefix && <span className="text-muted-foreground shrink-0">{opt.prefix}</span>}
                <span className="flex-1 truncate">{opt.label}</span>
                {opt.badge && <span className="text-[10px] text-muted-foreground shrink-0">{opt.badge}</span>}
              </button>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
